<template>
  <span class="archive-artwork" :style="{'--art-speed':playbackRate}" :class="{ 'has-started': started, 'is-running': running, 'is-complete': complete || reducedMotion }" @animationend="onAnimationEnd">
    <img v-if="staticArtwork" :src="fallback" width="1600" height="1180" :alt="alt" loading="lazy" decoding="async">
    <svg v-else :key="run" class="archive-artwork-drawing" viewBox="0 0 1600 1180" width="1600" height="1180" role="img" :aria-label="alt" v-html="markup" />
  </span>
</template>
<script>
import { archiveArtwork } from '../../data/archiveArtwork';
import { slidepoiseArtwork } from '../../data/slidepoiseArtwork';
const artworkAssets = new Map();
function readyAsset(url) {
  if(artworkAssets.has(url))return artworkAssets.get(url);
  const task=new Promise((resolve,reject)=>{
    const image=new Image();let timer;
    const finish=(error)=>{clearTimeout(timer);image.onload=null;image.onerror=null;error?reject(error):resolve();};
    image.onload=()=>finish();image.onerror=()=>finish(new Error('Artwork image unavailable'));
    timer=setTimeout(()=>finish(new Error('Artwork image timed out')),15000);image.src=url;
    if(image.complete&&image.naturalWidth)finish();
  }).catch(error=>{artworkAssets.delete(url);throw error;});
  artworkAssets.set(url,task);return task;
}
const compositions = { ...archiveArtwork, ...slidepoiseArtwork };
export default {
  name: 'AnimatedProjectArtwork',
  props: { composition: String, fallback: String, alt: String, paused: Boolean, run: Number, playbackRate:{type:Number,default:1} },
  emits: ['complete', 'motion-available'],
  data: () => ({ ready: false, visible: false, pageVisible: true, started: false, complete: false, reducedMotion: false, assetLoadFailed: false }),
  computed: {
    staticArtwork() { return this.reducedMotion || this.assetLoadFailed; },
    markup() { return compositions[this.composition].replaceAll('__ID__', `archive-${this.$.uid}-`); },
    running() { return this.started && this.visible && this.pageVisible && !this.paused && !this.complete && !this.reducedMotion; },
  },
  watch: {
    visible() { this.startWhenReady(); },
    ready() { this.startWhenReady(); },
    run() { this.complete = false; this.started = this.ready; },
  },
  mounted() {
    this._alive = true;
    this._motion = matchMedia('(prefers-reduced-motion: reduce)');
    this._onMotion = () => { this.reducedMotion = this._motion.matches; this.$emit('motion-available', !this.staticArtwork); };
    this._onMotion(); this._motion.addEventListener('change', this._onMotion);
    this._onVisibility = () => { this.pageVisible = !document.hidden; };
    this._onVisibility(); document.addEventListener('visibilitychange', this._onVisibility);
    this._observer = new IntersectionObserver(entries => { this.visible = entries[0].isIntersecting; }, { threshold: .25 });
    this._observer.observe(this.$el);
    this.prepareArtwork();
    this._size=new ResizeObserver(this.refreshVisibility);this._size.observe(this.$el);
  },
  activated(){this._alive=true;this.$nextTick(()=>{this._observer?.observe(this.$el);this.refreshVisibility();this.prepareArtwork();});},
  deactivated(){this.visible=false;this._observer?.unobserve(this.$el);},
  beforeUnmount() {
    this._alive = false;
    this._observer?.disconnect(); this._size?.disconnect();
    this._motion?.removeEventListener('change', this._onMotion);
    document.removeEventListener('visibilitychange', this._onVisibility);
  },
  methods: {
    refreshVisibility(){const rect=this.$el.getBoundingClientRect();this.visible=rect.width>0&&rect.height>0&&rect.bottom>0&&rect.top<window.innerHeight;this.startWhenReady();},
    async prepareArtwork(){
      if(this.ready||this._loading)return;this._loading=true;
      const urls=[...new Set([...this.markup.matchAll(/href="([^"]+)"/g)].map(match=>match[1]).filter(url=>!url.startsWith('#')))];
      try{await Promise.all(urls.map(readyAsset));if(this._alive){this.assetLoadFailed=false;this.ready=true;this.$emit('motion-available',!this.staticArtwork);this.refreshVisibility();}}
      catch{if(this._alive){this.assetLoadFailed=true;this.$emit('motion-available',false);}}
      finally{this._loading=false;}
    },
    startWhenReady() { if (this.ready && this.visible) this.started = true; },
    onAnimationEnd(event) { if (event.animationName === 'archive-sequence') { this.complete = true; this.$emit('complete'); } },
  },
};
</script>
<style>
.archive-artwork { position:relative; display:block; width:100%; aspect-ratio:1600/1180; }
.project-showcase .archive-artwork > img { position:absolute; inset:0; width:100%; height:100%; opacity:1; }
.project-showcase .archive-artwork-drawing { position:absolute; inset:0; width:100%; height:100%; stroke:none; fill:none; }
/* The first frame is the composition's paper and heading. Its pieces stay
   hidden while assets decode, so the finished poster never flashes first. */
.archive-artwork .archive-assemble,.archive-artwork .archive-thread { opacity:0; }
.archive-artwork.has-started .archive-artwork-drawing { opacity:1; animation:archive-sequence calc(6.2s / 1.5 / var(--art-speed)) linear both; }
.archive-artwork.has-started .archive-assemble { transform-box:fill-box; transform-origin:center; animation:archive-assemble calc(1s / var(--art-speed)) cubic-bezier(.22,.68,0,1) calc(var(--piece-delay) / 1.5 / var(--art-speed)) both; }
.archive-artwork.has-started .archive-thread { animation:archive-thread calc(1s / var(--art-speed)) cubic-bezier(.45,0,.2,1) calc(var(--piece-delay) / 1.5 / var(--art-speed)) both; }
.archive-artwork:not(.is-running) .archive-artwork-drawing,
.archive-artwork:not(.is-running) .archive-assemble,
.archive-artwork:not(.is-running) .archive-thread { animation-play-state:paused; }
.archive-artwork.is-complete .archive-assemble,
.archive-artwork.is-complete .archive-thread,
.archive-artwork.is-complete .archive-artwork-drawing { animation:none; opacity:1; }
@keyframes archive-assemble { from { opacity:0; transform:translate(var(--piece-x),var(--piece-y)) rotate(var(--piece-r)); } to { opacity:1; transform:translate(0,0) rotate(0); } }
@keyframes archive-thread { from { stroke-dasharray:1; stroke-dashoffset:1; opacity:0; } to { stroke-dasharray:1; stroke-dashoffset:0; opacity:1; } }
@keyframes archive-sequence { from { opacity:1; } to { opacity:1; } }
@media(prefers-reduced-motion:reduce) { .archive-artwork * { animation:none!important; } }
</style>
